// src/components/practice/NoExamSelected.jsx
// Safety guard component shown when user tries to access exam features without an exam selected
import React, { useState } from 'react';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import { AlertTriangle, Home, ArrowRight, CheckCircle, Check, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { auth } from '../../firebase';
import examService from '../../services/examService';
import LogamiLogoSpinner from '../ui/LogamiLogoSpinner';
import enhancedUserService from '../../services/enhancedUserService';

const NoExamSelected = ({ onBack, onExamSelected }) => {
  const navigate = useLocalizedNavigate();
  const { t } = useTranslation();
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Exam providers
  const examProviders = [
    {
      id: 'goethe',
      name: 'Goethe-Zertifikat',
      fullName: 'Goethe Institut',
      description: t('home.providers.goethe.desc', 'Internationally recognized German language certificate'),
      features: [
        t('home.providers.goethe.feature1', 'Comprehensive exam preparation'),
        t('home.providers.goethe.feature2', 'Official test format'),
        t('home.providers.goethe.feature3', 'Detailed feedback')
      ]
    },
    {
      id: 'osd',
      name: 'ÖSD Zertifikat',
      fullName: 'Österreichisches Sprachdiplom Deutsch',
      description: t('home.providers.osd.desc', 'Austrian German language certificate'),
      features: [
        t('home.providers.osd.feature1', 'Austrian German focus'),
        t('home.providers.osd.feature2', 'Official certification'),
        t('home.providers.osd.feature3', 'Comprehensive materials')
      ]
    },
    {
      id: 'telc',
      name: 'TELC Deutsch',
      fullName: 'The European Language Certificates',
      description: t('home.providers.telc.desc', 'European standard German certificate'),
      features: [
        t('home.providers.telc.feature1', 'European standard'),
        t('home.providers.telc.feature2', 'Widely accepted'),
        t('home.providers.telc.feature3', 'Practical focus')
      ]
    }
  ];

  // Available levels based on provider
  const getAvailableLevels = () => {
    if (!selectedProvider) return [];
    const levelsByProvider = {
      goethe: ['B1', 'B2', 'C1'],
      osd: ['B1', 'B2', 'C1'],
      telc: ['B1', 'B2', 'C1']
    };
    return levelsByProvider[selectedProvider] || ['B1', 'B2', 'C1'];
  };

  const handleExamSelect = async () => {
    if (!selectedProvider || !selectedLevel) return;

    setIsLoading(true);
    try {
      // Load the exam data
      const examData = await examService.loadExam(selectedProvider, selectedLevel);
      
      // Save exam data to localStorage
      const userId = auth.currentUser?.uid;
      if (userId) {
        localStorage.setItem(`selectedExamData_${userId}`, JSON.stringify(examData));
      }
      sessionStorage.setItem('selectedExamData', JSON.stringify(examData));
      localStorage.setItem('selectedExamData', JSON.stringify(examData));
      
      // Save to Firebase
      if (userId) {
        try {
          await enhancedUserService.saveExamSelection(userId, examData);
          console.log('Exam selection saved to Firebase');
        } catch (firebaseError) {
          console.error('Error saving to Firebase:', firebaseError);
          // Continue anyway - localStorage save succeeded
        }
      }
      
      // Call callback if provided
      if (onExamSelected) {
        await onExamSelected(examData);
      } else {
        // Trigger a custom event that App.jsx can listen to
        window.dispatchEvent(new CustomEvent('examSelected', { detail: examData }));
        
        // Reload the page to refresh exam data
        window.location.reload();
      }
    } catch (error) {
      console.error('Error loading exam:', error);
      alert('Error loading exam. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const availableLevels = getAvailableLevels();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        <div className="bg-white rounded-3xl shadow-2xl border-3 border-black p-8 sm:p-12 relative overflow-hidden">
          {/* Decorative corners */}
          <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 border-black/10 rounded-tl-3xl" />
          <div className="absolute bottom-0 right-0 w-16 h-16 border-b-4 border-r-4 border-black/10 rounded-br-3xl" />
          
          {/* Header */}
          <div className="text-center mb-8">
            {/* Warning Icon */}
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-10 h-10 text-amber-600" />
            </div>

            {/* Title */}
            <h1 className="font-space text-2xl sm:text-3xl font-black text-black mb-4">
              {t('exam.noExamSelected.title', 'No Exam Selected')}
            </h1>

            {/* Description */}
            <p className="text-black/70 mb-8 leading-relaxed max-w-2xl mx-auto">
              {t('exam.noExamSelected.description', 'It looks like you haven\'t selected an exam yet, or your exam selection was not loaded properly. Please choose your exam provider and level to continue.')}
            </p>
          </div>

          {/* Exam Provider Selection */}
          <div className="mb-8">
            <h3 className="font-space text-xl font-black text-black mb-6 text-center">
              {t('home.examSelection.provider', 'Select Exam Provider')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {examProviders.map((provider) => (
                <button
                  key={provider.id}
                  onClick={() => {
                    setSelectedProvider(provider.id);
                    setSelectedLevel(''); // Reset level when provider changes
                  }}
                  className={`group relative p-6 rounded-2xl border-3 transition-all duration-300 overflow-hidden ${
                    selectedProvider === provider.id
                      ? 'bg-black text-white border-black shadow-xl scale-105'
                      : 'bg-white text-black border-black/20 hover:border-black hover:shadow-lg'
                  }`}
                >
                  {/* Selection indicator */}
                  {selectedProvider === provider.id && (
                    <div className="absolute top-2 right-2 z-10">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                  )}

                  <div className="text-center">
                    <h4 className="font-space font-black text-lg mb-2">{provider.name}</h4>
                    <p className="text-sm opacity-80 mb-3">{provider.description}</p>
                    <div className="space-y-1">
                      {provider.features.slice(0, 2).map((feature, idx) => (
                        <div key={idx} className="flex items-center justify-center gap-1 text-xs">
                          <Check className={`w-3 h-3 ${selectedProvider === provider.id ? 'text-white' : 'text-black'}`} />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Level Selection */}
          {selectedProvider && (
            <div className="mb-8 animate-fade-in">
              <h3 className="font-space text-xl font-black text-black mb-6 text-center">
                {t('home.examSelection.level', 'Select Level')}
              </h3>
              <div className="flex flex-wrap justify-center gap-4">
                {availableLevels.map((level) => (
                  <button
                    key={level}
                    onClick={() => setSelectedLevel(level)}
                    className={`px-8 py-4 rounded-xl font-space font-black text-xl transition-all duration-300 ${
                      selectedLevel === level
                        ? 'bg-black text-white scale-110 shadow-xl'
                        : 'bg-white text-black border-2 border-black/20 hover:border-black hover:scale-105'
                    }`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Continue Button */}
          {selectedProvider && selectedLevel && (
            <div className="text-center mb-6">
              <button
                onClick={handleExamSelect}
                disabled={isLoading}
                className="group relative px-12 py-4 bg-black text-white font-space font-bold text-lg rounded-xl border-2 border-black overflow-hidden transition-all duration-300 hover:shadow-xl cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  {isLoading ? (
                    <>
                      <LogamiLogoSpinner size="md" decorative={false} alt="Logami loading" />
                      {t('common.loading', 'Loading...')}
                    </>
                  ) : (
                    <>
                      {t('home.examSelection.startButton', 'Continue')}
                      <ChevronRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                  )}
                </span>
              </button>
            </div>
          )}

          {/* Secondary Actions */}
          <div className="space-y-3 text-center">
            <button
              onClick={handleGoToDashboard}
              className="w-full px-8 py-3 bg-white text-black font-space font-medium text-base rounded-xl border-2 border-black/20 hover:border-black transition-all duration-300 cursor-pointer"
            >
              <span className="flex items-center justify-center gap-2">
                <Home className="w-5 h-5" />
                {t('exam.noExamSelected.goToDashboard', 'Go to Dashboard')}
              </span>
            </button>

            {/* Back button if provided */}
            {onBack && (
              <button
                onClick={onBack}
                className="w-full px-8 py-3 text-black/60 hover:text-black font-medium transition-colors duration-300 cursor-pointer"
              >
                {t('common.goBack', 'Go Back')}
              </button>
            )}
          </div>

          {/* Help text */}
          <p className="text-sm text-black/50 mt-8 text-center">
            {t('exam.noExamSelected.helpText', 'If this issue persists, please try logging out and back in, or contact support.')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default NoExamSelected;
